import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Wallet, ExternalLink, Send, RefreshCw, Copy, ShoppingCart, TrendingUp } from "lucide-react";
import { toast } from "sonner";

type Balance = { symbol: string; balance: number };

const WALLETS = [
  {
    key: "purchases",
    walletIndex: 0,
    title: "محفظة استقبال أموال الشراء",
    icon: ShoppingCart,
  },
  {
    key: "profits",
    walletIndex: 1,
    title: "محفظة استقبال الأرباح",
    icon: TrendingUp,
  },
] as const;

const WalletPanel = ({
  walletIndex,
  title,
  Icon,
}: {
  walletIndex: number;
  title: string;
  Icon: typeof ShoppingCart;
}) => {
  const [balances, setBalances] = useState<Balance[]>([]);
  const [address, setAddress] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [symbol, setSymbol] = useState<string>("");
  const [toAddress, setToAddress] = useState("");
  const [amount, setAmount] = useState("");
  const [estimating, setEstimating] = useState(false);
  const [sending, setSending] = useState(false);
  const [gasPol, setGasPol] = useState<number>(0);
  const [needsAutoGas, setNeedsAutoGas] = useState(false);
  const [tokenForGas, setTokenForGas] = useState(0);
  const [txHash, setTxHash] = useState<string>("");
  const [adminId, setAdminId] = useState<{ numeric_id: string; display_name: string } | null>(null);
  const [resolvedAddress, setResolvedAddress] = useState<string>("");
  const [resolvedName, setResolvedName] = useState<string>("");
  const [resolving, setResolving] = useState(false);

  const isId = /^\d{16}$/.test(toAddress.trim());
  const isAddr = /^0x[a-fA-F0-9]{40}$/.test(toAddress.trim());

  const currentBalance = balances.find((b) => b.symbol === symbol)?.balance || 0;

  const fetchBalances = async () => {
    setLoading(true);
    setTxHash("");
    try {
      const { data, error } = await supabase.functions.invoke("admin-central-send", {
        body: { action: "balances", walletIndex },
      });
      if (error || data?.error) throw new Error(data?.error || error?.message);
      setAddress(data.address || "");
      setBalances(data.balances || []);
      if (data.balances?.length && !symbol) setSymbol(data.balances[0].symbol);
    } catch (e: any) {
      toast.error(e.message || "فشل جلب الأرصدة");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBalances();
    (async () => {
      const { data } = await supabase
        .from("admin_wallet_identities")
        .select("numeric_id, display_name")
        .eq("wallet_index", walletIndex)
        .maybeSingle();
      if (data) setAdminId(data);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletIndex]);

  const estimate = async () => {
    if (!symbol) return null;
    setEstimating(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-central-send", {
        body: { action: "estimate", symbol, walletIndex },
      });
      if (error || data?.error) throw new Error(data?.error || error?.message);
      setGasPol(data.gasPol || 0);
      setNeedsAutoGas(!!data.needsAutoGas);
      setTokenForGas(Number(data.tokenForGas || 0));
      return data;
    } catch (e: any) {
      toast.error(e.message || "فشل تقدير الرسوم");
      return null;
    } finally {
      setEstimating(false);
    }
  };

  const handleMax = async () => {
    if (currentBalance <= 0) return;
    const est = await estimate();
    if (!est) return;
    let max = currentBalance;
    if (symbol === "POL") max = Math.max(0, currentBalance - Number(est.gasPol || 0));
    else if (est.needsAutoGas) max = Math.max(0, currentBalance - Number(est.tokenForGas || 0));
    setAmount(max > 0 ? max.toFixed(8).replace(/0+$/, "").replace(/\.$/, "") : "0");
  };

  // Resolve 16-digit ID → address
  useEffect(() => {
    const raw = toAddress.trim();
    setResolvedName("");
    if (isAddr) { setResolvedAddress(raw); return; }
    if (!isId) { setResolvedAddress(""); return; }
    let cancelled = false;
    setResolving(true);
    setResolvedAddress("");
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("tatum-eth-send", {
          body: { action: "lookup-internal", recipientId: raw },
        });
        if (cancelled) return;
        if (error || data?.error || !data?.found || !data?.address) {
          setResolvedAddress("");
          setResolvedName(data?.error || "لم يتم العثور على المستخدم");
        } else {
          setResolvedAddress(data.address);
          setResolvedName(data.name || "");
        }
      } finally {
        if (!cancelled) setResolving(false);
      }
    })();
    return () => { cancelled = true; };
  }, [toAddress, isAddr, isId]);

  const handleSend = async () => {
    const finalAddress = resolvedAddress;
    if (!symbol || !amount || !finalAddress) {
      toast.error("املأ جميع الحقول");
      return;
    }
    if (!/^0x[a-fA-F0-9]{40}$/.test(finalAddress)) {
      toast.error("عنوان غير صالح");
      return;
    }
    setSending(true);
    setTxHash("");
    try {
      const { data, error } = await supabase.functions.invoke("admin-central-send", {
        body: { action: "send", symbol, toAddress: finalAddress, amount: parseFloat(amount), walletIndex },
      });
      if (error || data?.error) throw new Error(data?.error || error?.message);
      setTxHash(data.txHash || "");
      toast.success("تم الإرسال بنجاح");
      setAmount("");
      setToAddress("");
      fetchBalances();
    } catch (e: any) {
      toast.error(e.message || "فشل الإرسال");
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="glass border-border">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-5 w-5 text-primary" /> {title}
        </CardTitle>
        <Button size="sm" variant="outline" onClick={fetchBalances} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {adminId && (
          <div className="space-y-1">
            <Label className="text-xs">معرّف الاستقبال الداخلي ({adminId.display_name})</Label>
            <div className="flex items-center gap-2 bg-primary/10 border border-primary/30 rounded p-2">
              <span className="text-sm text-foreground font-mono break-all flex-1" dir="ltr">{adminId.numeric_id}</span>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 shrink-0"
                onClick={() => {
                  navigator.clipboard.writeText(adminId.numeric_id);
                  toast.success("تم نسخ المعرّف");
                }}
              >
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground">يمكن للمستخدمين إرسال تحويل داخلي عبر هذا المعرّف.</p>
          </div>
        )}

        {address && (
          <div className="space-y-1">
            <Label className="text-xs">عنوان المحفظة (Polygon POS)</Label>
            <div className="flex items-center gap-2 bg-secondary/40 rounded p-2">
              <span className="text-xs text-foreground font-mono break-all flex-1" dir="ltr">{address}</span>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 shrink-0"
                onClick={() => {
                  navigator.clipboard.writeText(address);
                  toast.success("تم نسخ العنوان");
                }}
              >
                <Copy className="h-3.5 w-3.5" />
              </Button>
              <a
                href={`https://polygonscan.com/address/${address}`}
                target="_blank"
                rel="noreferrer"
                className="shrink-0"
              >
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0">
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              </a>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label>العملة (الأرصدة الحقيقية على السلسلة)</Label>
          {loading ? (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> جاري تحميل الأرصدة...
            </p>
          ) : balances.length === 0 ? (
            <p className="text-xs text-destructive">لا يوجد رصيد في هذه المحفظة على شبكة Polygon.</p>
          ) : (
            <Select value={symbol} onValueChange={setSymbol}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {balances.map((b) => (
                  <SelectItem key={b.symbol} value={b.symbol}>
                    {b.symbol} — <span className="font-mono">{b.balance.toFixed(6)}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="space-y-2">
          <Label>المستلم (معرّف 16 رقم أو عنوان 0x)</Label>
          <Input
            value={toAddress}
            onChange={(e) => setToAddress(e.target.value)}
            placeholder="0x... أو 16 رقم للمعرّف"
            dir="ltr"
            className="font-mono text-sm"
          />
          {resolving && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> جاري البحث عن المستخدم...
            </p>
          )}
          {isId && !resolving && resolvedAddress && (
            <div className="text-xs bg-green-500/10 border border-green-500/30 rounded p-2 space-y-1">
              <p className="text-green-500">✓ تم العثور على: {resolvedName}</p>
              <p className="font-mono break-all text-muted-foreground" dir="ltr">{resolvedAddress}</p>
            </div>
          )}
          {isId && !resolving && !resolvedAddress && resolvedName && (
            <p className="text-xs text-destructive">{resolvedName}</p>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>المبلغ ({symbol})</Label>
            <span className="text-xs text-muted-foreground">
              الرصيد: <span className="font-mono">{currentBalance.toFixed(6)}</span>
            </span>
          </div>
          <div className="flex gap-2">
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.0"
              dir="ltr"
              min="0"
              step="any"
              className="flex-1"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleMax}
              disabled={estimating || currentBalance <= 0}
            >
              {estimating ? <Loader2 className="w-3 h-3 animate-spin" /> : "الحد الأقصى"}
            </Button>
          </div>
          {needsAutoGas && tokenForGas > 0 && (
            <p className="text-xs text-yellow-500">
              ⚡ سيتم مبادلة تلقائية لـ ~{tokenForGas.toFixed(6)} {symbol} إلى POL لتغطية الغاز.
            </p>
          )}
          {gasPol > 0 && (
            <p className="text-xs text-muted-foreground">
              رسوم الشبكة: <span className="font-mono">{gasPol.toFixed(6)} POL</span>
            </p>
          )}
        </div>

        <Button onClick={handleSend} disabled={sending || !symbol || !amount || !resolvedAddress} className="w-full">
          {sending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin ml-2" /> جاري الإرسال...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 ml-2" /> إرسال
            </>
          )}
        </Button>

        {txHash && (
          <a
            href={`https://polygonscan.com/tx/${txHash}`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <ExternalLink className="w-3 h-3" /> عرض المعاملة على PolygonScan
          </a>
        )}
      </CardContent>
    </Card>
  );
};

const AdminCentralWalletSend = () => {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Wallet className="h-4 w-4 text-primary" />
        <span>محافظ المنصة على شبكة Polygon POS</span>
      </div>
      <Tabs defaultValue="purchases" className="w-full">
        <TabsList className="grid grid-cols-2 w-full">
          {WALLETS.map((w) => (
            <TabsTrigger key={w.key} value={w.key} className="gap-2">
              <w.icon className="h-4 w-4" /> {w.title}
            </TabsTrigger>
          ))}
        </TabsList>
        {WALLETS.map((w) => (
          <TabsContent key={w.key} value={w.key} className="mt-3">
            <WalletPanel walletIndex={w.walletIndex} title={w.title} Icon={w.icon} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default AdminCentralWalletSend;
